/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Account } from '@carbon/react/icons';
import { TextInput } from 'carbon-components-react';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { ControlledCheckbox } from '../../common/ControlledCheckbox';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { links } from '../links';
import { FIELDS } from '../schema';
import { AdvancedOptions as messages } from './messages';

export const AdvancedOptions = ({ readOnly }: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { registerInputField } = useFormHelpers(formMethods);

  const failureReplyChangeState = useState<boolean>(false);
  const providerUrlChangeState = useState<boolean>(false);

  return (
    <TabContent
      ImageComponent={() => <Account size={32} />}
      title={<FormattedMessage {...messages.title} />}>
      <div className="TabContent__body-content TabContent__field-container--full-width">
        <div className="TabContent__subtitle TabContent__link-larger-text">
          <FormattedMessageWithLink
            url={
              useIsNewIA()
                ? links.learnMoreAdvancedOptionsNewIA
                : links.learnMoreAdvancedOptions
            }
            {...messages.subtitle}
          />
        </div>
        <div className="TabContent__spacer16" />
        <div className="TabContent__field-container">
          <ControlledCheckbox
            field={FIELDS.CREATE_SESSION_VIA_INBOUND_MESSAGES}
            labelText={t(messages.inbound)}
            readOnly={readOnly}
          />
        </div>
        <div className="TabContent__spacer16" />
        <div className="TabContent__field-container">
          <TextInput
            {...registerInputField({
              field: FIELDS.MESSAGE_FAILURE_REPLY_MESSAGE,
              labelText: messages.errorReplyMessage,
              changeState: failureReplyChangeState,
              readOnly
            })}
          />
        </div>
        <div className="TabContent__field-container">
          <TextInput
            {...registerInputField({
              field: FIELDS.PROVIDER_URL,
              labelText: messages.urlLabel,
              placeholderText: messages.urlPlaceholder,
              changeState: providerUrlChangeState,
              readOnly
            })}
          />
        </div>
        <div className="TabContent__spacer24" />
      </div>
    </TabContent>
  );
};
